/* =========================================
   Product Details Tab
   Dwell Theme Compatible
========================================= */

if (!customElements.get("product-details-tab")) {
  class ProductDetailsTab extends HTMLElement {
    constructor() {
      super();

      this.tabs = [];
      this.images = [];
      this.tabContents = [];
      this.tooltipTriggers = [];

      this.handleTabClick = this.handleTabClick.bind(this);
      this.handleTabKeydown = this.handleTabKeydown.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleTooltipKeydown = this.handleTooltipKeydown.bind(this);
    }

    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      this.destroy();
    }

    init() {
      this.destroy();

      this.tabs = Array.from(
        this.querySelectorAll("[data-pdt-tab]")
      );

      this.images = Array.from(
        this.querySelectorAll("[data-pdt-image]")
      );

      this.tabContents = Array.from(
        this.querySelectorAll("[data-pdt-content]")
      );

      this.tooltipTriggers = Array.from(
        this.querySelectorAll(".sf__tooltip-trigger")
      );

      if (!this.tabs.length) return;

      this.tabs.forEach((tab) => {
        tab.addEventListener("click", this.handleTabClick);
        tab.addEventListener("keydown", this.handleTabKeydown);
      });

      this.tooltipTriggers.forEach((trigger) => {
        trigger.addEventListener(
          "keydown",
          this.handleTooltipKeydown
        );
      });

      this.addEventListener(
        "shopify:block:select",
        this.handleBlockSelect
      );

      const activeTab =
        this.tabs.find((tab) =>
          tab.classList.contains("is-active")
        ) || this.tabs[0];

      this.activateTab(this.getTabKey(activeTab), {
        focus: false,
        scroll: false,
        emit: false
      });
    }

    destroy() {
      this.tabs.forEach((tab) => {
        tab.removeEventListener("click", this.handleTabClick);
        tab.removeEventListener("keydown", this.handleTabKeydown);
      });

      this.tooltipTriggers.forEach((trigger) => {
        trigger.removeEventListener(
          "keydown",
          this.handleTooltipKeydown
        );
      });

      this.removeEventListener(
        "shopify:block:select",
        this.handleBlockSelect
      );

      this.tabs = [];
      this.images = [];
      this.tabContents = [];
      this.tooltipTriggers = [];
    }

    getTabKey(tab) {
      if (!tab) return "";

      const dataKey = tab.dataset.pdtTab;

      if (dataKey) return dataKey;

      const index = this.tabs.indexOf(tab);

      return index >= 0 ? `index-${index}` : "";
    }

    getItemKey(item, collection) {
      if (!item) return "";

      const dataKey =
        item.dataset.pdtImage ||
        item.dataset.pdtContent;

      if (dataKey) return dataKey;

      const index = collection.indexOf(item);

      return index >= 0 ? `index-${index}` : "";
    }

    handleTabClick(event) {
      const tab = event.currentTarget;

      this.activateTab(this.getTabKey(tab), {
        focus: false,
        scroll: true
      });
    }

    handleTabKeydown(event) {
      const currentIndex = this.tabs.indexOf(
        event.currentTarget
      );

      if (currentIndex < 0) return;

      let nextIndex = null;

      switch (event.key) {
        case "ArrowRight":
        case "ArrowDown":
          nextIndex =
            (currentIndex + 1) % this.tabs.length;
          break;

        case "ArrowLeft":
        case "ArrowUp":
          nextIndex =
            (currentIndex - 1 + this.tabs.length) %
            this.tabs.length;
          break;

        case "Home":
          nextIndex = 0;
          break;

        case "End":
          nextIndex = this.tabs.length - 1;
          break;

        case "Enter":
        case " ":
          event.preventDefault();
          this.activateTab(
            this.getTabKey(event.currentTarget),
            {
              focus: true,
              scroll: true
            }
          );
          return;

        default:
          return;
      }

      event.preventDefault();

      const nextTab = this.tabs[nextIndex];

      this.activateTab(this.getTabKey(nextTab), {
        focus: true,
        scroll: true
      });
    }

    handleBlockSelect(event) {
      const blockId =
        event.detail?.blockId ||
        event.target?.dataset?.pdtContent ||
        event.target
          ?.closest?.("[data-pdt-content]")
          ?.dataset?.pdtContent;

      if (!blockId) return;

      const matchingTab = this.tabs.find(
        (tab) => this.getTabKey(tab) === blockId
      );

      if (!matchingTab) return;

      this.activateTab(blockId, {
        focus: false,
        scroll: true
      });
    }

    handleTooltipKeydown(event) {
      if (event.key !== "Escape") return;

      event.preventDefault();
      event.currentTarget.blur();
    }

    activateTab(tabKey, options = {}) {
      const {
        focus = false,
        scroll = false,
        emit = true
      } = options;

      if (!tabKey) return;

      const activeTab = this.tabs.find(
        (tab) => this.getTabKey(tab) === tabKey
      );

      if (!activeTab) return;

      this.tabs.forEach((tab) => {
        const isActive =
          this.getTabKey(tab) === tabKey;

        tab.classList.toggle("is-active", isActive);
        tab.setAttribute(
          "aria-selected",
          isActive ? "true" : "false"
        );
        tab.setAttribute(
          "tabindex",
          isActive ? "0" : "-1"
        );
      });

      this.images.forEach((image) => {
        const isActive =
          this.getItemKey(image, this.images) === tabKey;

        image.classList.toggle("is-active", isActive);
        image.setAttribute(
          "aria-hidden",
          isActive ? "false" : "true"
        );
        image.hidden = !isActive;
      });

      this.tabContents.forEach((content) => {
        const isActive =
          this.getItemKey(
            content,
            this.tabContents
          ) === tabKey;

        content.classList.toggle("is-active", isActive);
        content.setAttribute(
          "aria-hidden",
          isActive ? "false" : "true"
        );
        content.hidden = !isActive;
      });

      if (focus) {
        activeTab.focus({
          preventScroll: true
        });
      }

      if (scroll) {
        this.scrollTabIntoView(activeTab);
      }

      if (emit) {
        this.dispatchEvent(
          new CustomEvent(
            "product-details-tab:change",
            {
              bubbles: true,
              detail: {
                tabId: tabKey
              }
            }
          )
        );
      }
    }

    scrollTabIntoView(tab) {
      const tabsContainer = tab.closest(
        ".product-details-tab__tabs-inner"
      );

      if (!tabsContainer) return;

      if (
        tabsContainer.scrollWidth <=
        tabsContainer.clientWidth
      ) {
        return;
      }

      const reduceMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

      tab.scrollIntoView({
        behavior: reduceMotion ? "auto" : "smooth",
        block: "nearest",
        inline: "nearest"
      });
    }
  }

  customElements.define(
    "product-details-tab",
    ProductDetailsTab
  );
}
